<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'

import { useI18n, type MessageKey } from '@/app/i18n'
import { navTarget, type NavItem } from '@/app/nav/collect'
import { sharedViewMeta } from '@/app/router/shared-view'
import { useCountsStore } from '@/app/stores/counts'
import type { SoftwareCode } from '@/app/stores/session'
import AppBadge from '@/shared/ui/AppBadge.vue'
import AppCollapse from '@/shared/ui/AppCollapse.vue'
import AppIcon from '@/shared/ui/AppIcon.vue'

/**
 * One entry of the vertical nav (legacy `VerticalNavMenuLink` / `VerticalNavMenuGroup`, wp/shell §4.18): a link, or a
 * group that expands on click (one open at a time, via the parent `AppCollapseGroup`) and opens itself when one of its
 * links is the current page. Counters come from the badge store; a group's counter is its own `badge` (Finances).
 */
const props = defineProps<{ item: NavItem; software: SoftwareCode; mini: boolean; depth?: number }>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const counts = useCountsStore()

function resolvedName(target: RouteLocationRaw | undefined): string | null {
  if (!target) return null
  try {
    const name = router.resolve(target).name
    return typeof name === 'string' ? name : null
  } catch {
    return null
  }
}

function isActive(item: NavItem): boolean {
  const target = navTarget(item, props.software)
  if (!target) return false
  const name = resolvedName(target)
  // A route shared with this software highlights its own entry (`meta.sharedView`, queue #28).
  if (name && (route.name === name || sharedViewMeta(route.meta, props.software).navActiveLink === name)) return true
  const path = router.resolve(target).path
  return route.path === path
}

const target = computed(() => navTarget(props.item, props.software))
const active = computed(() => isActive(props.item))
const childActive = computed(() => props.item.children?.some((child) => isActive(child)) ?? false)
const badge = computed(() => (props.item.badge ? counts.badge(props.item.badge) : 0))
// Titles are message keys declared with keys.t() in the nav files.
// eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
const label = computed(() => t(props.item.title as MessageKey))

const open = ref(childActive.value)
watch(childActive, (value) => {
  if (value) open.value = true
})
</script>

<template>
  <li
    v-if="item.children?.length"
    class="nav-group"
    :class="{ 'is-open': open, 'is-active': childActive }"
    :data-nav="item.id"
  >
    <AppCollapse :id="`nav-${item.id}`" v-model:open="open">
      <template #header="{ toggle, controls }">
        <button type="button" class="nav-link" :aria-expanded="open" :aria-controls="controls" @click="toggle">
          <AppIcon :name="item.icon || 'CircleIcon'" :size="18" class="nav-link__icon" />
          <span class="nav-link__title">{{ label }}</span>
          <AppBadge v-if="badge" :value="badge" variant="danger" class="nav-link__badge" data-testid="nav-badge" />
          <AppIcon name="ChevronRightIcon" :size="16" class="nav-link__arrow" />
        </button>
      </template>
      <ul v-show="!mini" class="nav-group__items">
        <NavMenuItem
          v-for="child in item.children"
          :key="child.id"
          :item="child"
          :software="software"
          :mini="mini"
          :depth="(depth ?? 0) + 1"
        />
      </ul>
    </AppCollapse>
  </li>
  <li v-else class="nav-item" :class="{ 'is-active': active }" :data-nav="item.id">
    <RouterLink v-if="target" :to="target" class="nav-link" :aria-current="active ? 'page' : undefined">
      <AppIcon :name="item.icon || 'CircleIcon'" :size="depth ? 14 : 18" class="nav-link__icon" />
      <span class="nav-link__title">{{ label }}</span>
      <AppBadge v-if="badge" :value="badge" variant="danger" class="nav-link__badge" data-testid="nav-badge" />
    </RouterLink>
  </li>
</template>
